import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-29b32915"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  id: "campaign-complete",
  class: /*#__PURE__*/_normalizeClass({ portrait: true })
}
const _hoisted_2 = { id: "campaign-complete-media-container" }
const _hoisted_3 = { id: "campaign-complete-info-container" }
const _hoisted_4 = { id: "campaign-complete-info" }
const _hoisted_5 = { id: "campaign-complete-info-header" }
const _hoisted_6 = {
  id: "campaign-complete-info-header-title",
  "data-test": "claim-name"
}
const _hoisted_7 = { id: "campaign-complete-info-header-subtitle" }
const _hoisted_8 = ["data-wallet"]
const _hoisted_9 = ["data-creator-address"]
const _hoisted_10 = { id: "campaign-complete-info-summary-countdown-action" }

import { Interface } from '@ethersproject/abi';
import { TransactionResponse } from '@ethersproject/abstract-provider';
import { BigNumber } from '@ethersproject/bignumber';
import { useMutation } from '@tanstack/vue-query';
import JSConfetti from 'js-confetti';
import { computed, ref } from 'vue';
import { erc721Abi, erc1155Abi } from '@/abis';
import Action from '@/components/Action.vue';
import ClaimDescription from '@/components/ClaimDescription.vue';
import ClaimSummary from '@/components/ClaimSummary.vue';
import ConfirmMintDialog from '@/components/ConfirmMintDialog.vue';
import Countdown from '@/components/Countdown.vue';
import Footer from '@/components/Footer.vue';
import Frame from '@/components/Frame.vue';
import ImageLoader from '@/components/ImageLoader.vue';
import PlatformLinks from '@/components/PlatformLinks.vue';
import Provenance, { Record } from '@/components/Provenance.vue';
import WalletConnection from '@/components/WalletConnection.vue';
import {
  abbreviate,
  abbreviateAddress,
  isJSON,
  sleep
} from '@/lib/claimUtil';
import * as telemetry from '@/lib/telemetry';
import { useClaimStore } from '@/store/claimStore';
import ViewOriginalMedia from './ViewOriginalMedia.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ClaimCompleteBody',
  setup(__props) {

const store = useClaimStore();
const numberToMint = ref('1');
const txLink = ref('');
const mintedTokenId = ref<BigNumber>();

const error = computed(() => {
  closeConfirmMintDialog();
  setDialogNotConfirming();

  if (!mint.isError.value || !mint.error.value) {
    return null;
  }

  const err: any = mint.error.value;
  let msg;
  if (err.code === 4001 || err.code === 'ACTION_REJECTED') {
    // don't show error when user rejects
    return null;
  } else if (err.code === 'INSUFFICIENT_FUNDS') {
    msg = 'Not enough funds in wallet';
  } else if (
    err.code === 'TRANSACTION_REPLACED' &&
      !err.cancelled &&
      err.replacement
  ) {
    // Check if the trasaction was sped up
    msg = `Transaction was replaced, please check https://etherscan.io/tx/${err.replacement.hash} for the status of your transaction`;
  } else if (err.code === 'TRANSACTION_REPLACED' && err.cancelled) {
    msg = 'Transaction was cancelled';
  } else if (err.code === 'CALL_EXCEPTION') {
    msg = 'Transaction Failed';
  } else if (err.code === 'UNPREDICTABLE_GAS_LIMIT' && isJSON(err.error?.body)) {
    // providers return errors where error.message is not end-user readable and
    // containing both json and plain text. Further parsing of the error.body is required
    // to get a human readable message.
    msg = JSON.parse(err.error.body).error?.message?.replace('execution reverted: ', '') || 'The simulation for your transaction predicted a failure. Refresh the page and try again.';
  } else if (err.message && err.message.includes('Ledger device')) {
    msg = 'Error with your ledger device, please try again';
  } else if (
    err.message &&
      err.message.includes('Maximum tokens already minted')
  ) {
    msg = 'You already minted the maximum allowable quantity. Try secondary!';
  } else if (err.error && err.error.message) {
    msg = err.error.message;
  } else if (
    err.error &&
      typeof err.error === 'string' &&
      err.error.includes('Address is not part of audience')
  ) {
    msg = 'You are not on the allow list for this claim';
  } else if (err.error) {
    msg = err.error;
  } else if (err.message) {
    msg = err.message;
  } else if (typeof err === 'string') {
    msg = err;
  } else {
    msg = 'Something went wrong.';
  }

  return {
    error: msg
  };
});

const confirmMintDialogOpen = ref(false);
const isConfirming = ref(false);
const setDialogConfirming = () => {
  isConfirming.value = true;
};
const setDialogNotConfirming = () => {
  isConfirming.value = false;
};
const closeConfirmMintDialog = () => {
  confirmMintDialogOpen.value = false;
};
const openConfirmMintDialog = () => {
  confirmMintDialogOpen.value = true;
};

const mint = useMutation(async () => {
  if (!store.isConnected || !store.mintForWallet) {
    throw new Error('Connect your wallet to continue');
  }

  const numberToMintInt = parseInt(numberToMint.value);

  setDialogNotConfirming(); // should never be true here, but we know it should be false so can't hurt
  openConfirmMintDialog();

  // TODO: why is this | undefined?
  let response: TransactionResponse | undefined;
  if (numberToMintInt === 1) {
    response = await store.contract.mint(
      store.claimableMintIndices[0] || 0,
      store.claimableMerkleProofs[0] || [],
      store.cost,
      store.mintForWallet
    );
  } else {
    response = await store.contract.mintBatch(
      numberToMintInt,
      store.claimableMintIndices.slice(0, numberToMintInt),
      store.claimableMerkleProofs.slice(0, numberToMintInt),
      store.cost.mul(numberToMintInt),
      store.mintForWallet
    );
  }

  // Tx was not rejected, change dialog state to confirming
  setDialogConfirming();

  if (!store.isProviderAvailable) {
    // HACK: since claims supports minting without a provider
    // (e.g. walletconnect) we don't have the ability
    // to wait until the transaction has been mined.
    // Pausing here allows us to have a good chance
    // that the block will be mined before a ui
    // refresh happens which avoids a number of
    // confusing ui edge cases.
    await sleep(60_000);
  } else {
    try {
      txLink.value = response.hash;
      const receipt = await response.wait(1);
      if (receipt && receipt.logs) {
        const ids = [];
        for (const log of receipt.logs) {
          try {
            if (store.is721) {
              const iface = new Interface(erc721Abi);
              const parsed = iface.parseLog(log);
              ids.push(parsed.args.tokenId);
            } else {
              const iface = new Interface(erc1155Abi);
              const parsed = iface.parseLog(log);
              ids.push(parsed.args.id);
            }
          } catch (e) {
          // Do nothing, log will failed parse for events specific to extension contract
          }
        }
        mintedTokenId.value = ids[ids.length - 1];
      }
    } catch (e) {
    // tx is already complete; no need to throw
      console.error(e);
    }
  }

  closeConfirmMintDialog();
  setDialogNotConfirming();

  new JSConfetti().addConfetti({
    emojis: ['🎉'],
    confettiRadius: 90,
    confettiNumber: 200
  });

  telemetry.minted(numberToMintInt);
  store.fetchWeb3State();
  numberToMint.value = '1';
  return response;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ConfirmMintDialog, {
      "is-open": confirmMintDialogOpen.value,
      "mint-count": parseInt(numberToMint.value),
      "is-confirming": isConfirming.value
    }, null, 8, ["is-open", "mint-count", "is-confirming"]),
    _createVNode(Frame, {
      id: "campaign-complete-frame",
      class: "m-project"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(WalletConnection, {
            id: "campaign-complete-wallet",
            "fallback-provider": _unref(store).fallbackProvider
          }, null, 8, ["fallback-provider"]),
          _createElementVNode("div", _hoisted_2, [
            _createVNode(ImageLoader, {
              id: "campaign-complete-media",
              image: _unref(store).image,
              media: _unref(store).animation ?? _unref(store).tokenUrl ?? null,
              "arweave-url": _unref(store).arweaveURL ?? null
            }, {
              error: _withCtx(() => [
                _createTextVNode(" Failed to load the image ")
              ]),
              _: 1
            }, 8, ["image", "media", "arweave-url"])
          ]),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("h1", _hoisted_6, _toDisplayString(_unref(store).name), 1),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", {
                    id: "campaign-complete-info-header-artist",
                    "data-widget": "m-wallet-identity",
                    class: "manifold-scheme-dark",
                    "data-wallet": _unref(store).creator.address
                  }, null, 8, _hoisted_8),
                  _createElementVNode("div", {
                    id: "campaign-complete-info-header-subscribe",
                    "data-widget": "m-widget-subscribe",
                    "data-creator-address": _unref(store).creator?.address
                  }, null, 8, _hoisted_9)
                ])
              ]),
              _createVNode(ClaimDescription, {
                id: "campaign-complete-info-description",
                description: _unref(store).description
              }, null, 8, ["description"]),
              _createElementVNode("div", _hoisted_10, [
                _createVNode(ClaimSummary, {
                  id: "campaign-complete-info-summary",
                  total: _unref(store).total,
                  "total-max": _unref(store).totalMax,
                  cost: _unref(store).cost,
                  "has-allowlist": _unref(store).hasAllowlist
                }, null, 8, ["total", "total-max", "cost", "has-allowlist"]),
                (_unref(store).status === 'active' && _unref(store).endDate && !_unref(store).isSoldOut)
                  ? (_openBlock(), _createBlock(Countdown, {
                      key: 0,
                      id: "campaign-complete-info-countdown",
                      "end-date": _unref(store).endDate
                    }, null, 8, ["end-date"]))
                  : _createCommentVNode("", true),
                _createVNode(Action, {
                  id: "campaign-complete-info-action",
                  error: _unref(error),
                  "mint-status": _unref(mint).status,
                  "tx-link": txLink.value,
                  "minted-token-id": mintedTokenId.value,
                  mint: _unref(mint).mutate,
                  "number-to-mint": numberToMint.value,
                  onNumberToMintChanged: _cache[0] || (_cache[0] = ($event: any) => (numberToMint.value = $event))
                }, null, 8, ["error", "mint-status", "tx-link", "minted-token-id", "mint", "number-to-mint"])
              ]),
              _createVNode(Provenance, {
                id: "campaign-complete-info-details",
                records: ([
                        //TODO: make storageprotocol aware
                        _unref(store).arweaveURL ? {
                          label: 'Arweave URL',
                          link: _unref(store).arweaveURL,
                          value: _unref(abbreviate)(_unref(store).arweaveURL.split('/').pop()!)
                        } : undefined,
                        {
                          label: 'Contract Address',
                          link: `https://etherscan.io/address/${_unref(store).creatorContractAddress}`,
                          value: _unref(abbreviateAddress)(_unref(store).creatorContractAddress)
                        },
                        _unref(store).claimType ? {
                          label: 'Token Standard',
                          value: _unref(store).claimType.toUpperCase()
                        } : undefined,
                        {
                          label: 'Blockchain',
                          value: 'Ethereum'
                        },
                        _unref(store).startDate ? {
                          label: 'Start Date',
                          value: _unref(store).startDate.toISOString().slice(0, 10)
                        } : undefined,
                        _unref(store).endDate ? {
                          label: 'End Date',
                          value: _unref(store).endDate.toISOString().slice(0, 10)
                        } : undefined
                      ].filter(Boolean) as Record[])
              }, null, 8, ["records"]),
              _createVNode(PlatformLinks, {
                id: "campaign-complete-info-platform-links",
                is1155: _unref(store).is1155,
                "has-mints": _unref(store).total > 0,
                "creator-contract-address": _unref(store).creatorContractAddress,
                "token-id": _unref(store).tokenId === null ? undefined : _unref(store).tokenId
              }, null, 8, ["is1155", "has-mints", "creator-contract-address", "token-id"]),
              (_unref(store).arweaveURL)
                ? (_openBlock(), _createBlock(ViewOriginalMedia, {
                    key: 0,
                    "arweave-url": _unref(store).arweaveURL ?? null
                  }, null, 8, ["arweave-url"]))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        _createVNode(Footer)
      ]),
      _: 1
    })
  ], 64))
}
}

})